import { WModalButton } from "@/components/button/WToolButton.tsx";
import FF from "@/components/forms/FormField.module.css";
import TF from "@/components/forms/TextField.module.css";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { controllerProps, formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { vmCloneMutation, type VirtualMachineLoc } from "@/modules/compute/vmQuery.ts";
import { cn } from "@/utils/classNames.ts";
import { useGenerateUuid } from "@/utils/useGenerateUuid.ts";
import { useLingui } from "@lingui/react/macro";
import { Input } from "react-aria-components";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";

interface VmCloneInputs {
    name: string;
}

function VmCloneModalContent({ vm }: { vm: VirtualMachineLoc }) {
    const { name: originalVmName, uuid, location } = vm;

    //#region Hooks
    const { t } = useLingui();
    const cloneMutation = useStandardMutation(vmCloneMutation);
    const requestUuid = useGenerateUuid(undefined);
    //#endregion

    const onSubmit: SubmitHandler<VmCloneInputs> = async (inputs) => {
        const { name } = inputs;

        await cloneMutation.mutateAsync({
            location,
            requestUuid,
            body: {
                uuid,
                name,
            },
        });
    };

    const form = useForm<VmCloneInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            name: `${originalVmName}-clone`,
        },
    });

    const { control } = form;

    return (
        <WModalForm form={form} onSubmit={onSubmit} title={t`Clone resource`} label={t`Clone`}>
            <Controller
                control={control}
                name="name"
                {...controllerProps({ isRequired: true })}
                render={(props) => (
                    <WTextField
                        {...formPropsToAriaProps(props)}
                        autoFocus
                        isRequired
                        headless
                        block
                        label={t`Resource name`}
                    >
                        <Input className={cn(FF.FormFieldInput, TF.Input)} />
                    </WTextField>
                )}
            />
        </WModalForm>
    );
}

export function VmCloneModal({ vm }: { vm: VirtualMachineLoc }) {
    const { t } = useLingui();
    return (
        <WModal button={<WModalButton label={t`Clone`} icon="jp-duplicate-icon" />}>
            <VmCloneModalContent vm={vm} />
        </WModal>
    );
}
